import { call, put, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { LocalStoreDocumentInfo } from 'generated/bffDocument/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import { decodeBase64, downloadFile } from 'helpers/downloadHandler';
import {
  createActionType,
  createApiActionCreators,
  createReducer,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { getKahlActions } from 'saga/kahl/ducks';

export interface ExtendedLocalStoreDocumentInfo extends LocalStoreDocumentInfo {
  subtitle?: string;
  documentGUID?: string;
  objectStoreName?: string;
}

/* STATE */
export interface LocalDocumentsState {
  documents: ExtendedLocalStoreDocumentInfo[];
}

/* ACTION TYPES */
export enum LocalDocumentsActionTypes {
  Download = '@@LOCAL_STORE_DOCUMENT/DOWNLOAD',
  Upload = '@@LOCAL_STORE_DOCUMENT/UPLOAD',
  StaticDocumentUpload = '@@LOCAL_STORE_DOCUMENT/STATIC_DOCUMENT_UPLOAD',
  Delete = '@@LOCAL_STORE_DOCUMENT/DELETE',
  Fetch = '@@LOCAL_STORE_DOCUMENT/FETCH',
}

/* ACTIONS */
export const downloadActions = createApiActionCreators(LocalDocumentsActionTypes.Download);
export const uploadActions = createApiActionCreators(LocalDocumentsActionTypes.Upload);
export const staticDocumentUploadActions = createApiActionCreators(LocalDocumentsActionTypes.StaticDocumentUpload);
export const deleteActions = createApiActionCreators(LocalDocumentsActionTypes.Delete);
export const fetchActions = createApiActionCreators(LocalDocumentsActionTypes.Fetch);

/* REDUCERS */
const initialState: LocalDocumentsState = {
  documents: [],
};

export default createReducer(initialState, {
  [LocalDocumentsActionTypes.Upload]: {
    [RequestActionTypes.SUCCESS]: (state: LocalDocumentsState, payload: ExtendedLocalStoreDocumentInfo) => ({
      ...state,
      documents: [...state.documents, payload],
    }),
  },
  [LocalDocumentsActionTypes.StaticDocumentUpload]: {
    [RequestActionTypes.SUCCESS]: (state: LocalDocumentsState, payload: ExtendedLocalStoreDocumentInfo) => ({
      ...state,
      documents: [...state.documents, payload],
    }),
  },
  [LocalDocumentsActionTypes.Fetch]: {
    [RequestActionTypes.SUCCESS]: (state: LocalDocumentsState, payload: ExtendedLocalStoreDocumentInfo[]) => ({
      ...state,
      documents: payload,
    }),
  },
  [LocalDocumentsActionTypes.Delete]: {
    [RequestActionTypes.SUCCESS]: (state: LocalDocumentsState, fileRefNo: string) => ({
      ...state,
      documents: state.documents.filter((doc) => doc.fileRefNo !== fileRefNo),
    }),
  },
});

/* SELECTORS */
const selectLocalStoreDocuments = (state: AppState) => state.localStoreDocuments;

export const selectDocuments = (state: AppState) => selectLocalStoreDocuments(state).documents;

/* SAGAS */
function* upload({ payload }: any) {
  const { file, params } = payload;

  const request: ApiClientRequest = {
    apiCall: call(api.uploadLocalDocument, yield getApiUrl(environment.urlConfig.documents), file, params),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    const { localStoreDocumentInfo } = resp.data;
    const subtitle = (localStoreDocumentInfo.properties || []).find(
      (p: Record<string, string>) => p.name === 'CustomerDocSubtype'
    )?.value;

    yield put(
      uploadActions.success({
        ...localStoreDocumentInfo,
        subtitle,
      })
    );
  } else {
    yield put(getKahlActions.request({ infoMessageIds: resp.data.failures[0].contextData.messageIds }));
    yield put(uploadActions.failure());
  }
}

function* uploadStaticDocumentToLocalStore({ payload }: any) {
  const {
    directDmsMode,
    extId,
    entityKey,
    uploadedFileName,
    objectStoreName,
    appliedTo,
    fileType,
    customerRefNo,
    crRefNo,
    orderRefNo,
    contentElementNumber,
    subtitle,
    documentGUID,
    objectStoreId,
    contactPersonRefNo,
  } = payload;

  const request: ApiClientRequest = {
    apiCall: call(
      api.uploadStaticDocumentToLocalStore,
      yield getApiUrl(environment.urlConfig.documents),
      fileType,
      directDmsMode,
      extId,
      entityKey,
      uploadedFileName,
      appliedTo,
      objectStoreId ? objectStoreId : objectStoreName,
      contentElementNumber,
      customerRefNo,
      crRefNo,
      orderRefNo,
      contactPersonRefNo
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(
      staticDocumentUploadActions.success({
        ...resp.data.localStoreDocumentInfo,
        subtitle,
        objectStoreName,
        documentGUID,
      })
    );
  } else {
    yield put(getKahlActions.request({ infoMessageIds: resp.data.failures[0].contextData.messageIds }));
    yield put(staticDocumentUploadActions.failure());
  }
}

function* fetch({ payload }: any) {
  console.log(payload);

  const { directDmsMode, entityKey, filter } = payload;

  const request: ApiClientRequest = {
    apiCall: call(
      api.fetchLocalDocuments,
      yield getApiUrl(environment.urlConfig.documents),
      directDmsMode,
      entityKey,
      filter
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(fetchActions.success(resp.data.documentList));
  } else {
    yield put(fetchActions.failure());
  }
}

function* remove({ payload }: any) {
  const { fileRefNo, directDmsMode, entityKey, extId } = payload;

  if (fileRefNo) {
    const request: ApiClientRequest = {
      apiCall: call(
        api.deleteLocalStoreDocumentWithRefNo,
        yield getApiUrl(environment.urlConfig.documents),
        fileRefNo,
        directDmsMode,
        entityKey
      ),
    };
    const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

    if (resp.ok) {
      yield put(deleteActions.success(fileRefNo));
    } else {
      yield put(deleteActions.failure());
    }
  } else {
    const request: ApiClientRequest = {
      apiCall: call(
        api.deleteLocalStoreDocument,
        yield getApiUrl(environment.urlConfig.documents),
        directDmsMode,
        entityKey,
        extId,
        'JZD'
      ),
    };
    const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

    if (resp.ok) {
      yield put(deleteActions.success(extId));
    } else {
      yield put(deleteActions.failure());
    }
  }
}

function* download({ payload }: any) {
  const { fileRefNo, entityKey } = payload;

  const request: ApiClientRequest = {
    apiCall: call(
      api.getLocalStoreDocumentDetail,
      yield getApiUrl(environment.urlConfig.documents),
      fileRefNo,
      entityKey
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    const { content, fileName, mediaType } = resp.data;

    downloadFile(decodeBase64(content), fileName, mediaType);
  } else {
    yield put(downloadActions.failure());
  }
}

/* EXPORT */
export function* localStoreDocumentSaga() {
  yield takeLatest(createActionType(LocalDocumentsActionTypes.Upload, RequestActionTypes.REQUEST), upload);
  yield takeEvery(
    createActionType(LocalDocumentsActionTypes.StaticDocumentUpload, RequestActionTypes.REQUEST),
    uploadStaticDocumentToLocalStore
  );
  yield takeLeading(createActionType(LocalDocumentsActionTypes.Fetch, RequestActionTypes.REQUEST), fetch);
  yield takeLatest(createActionType(LocalDocumentsActionTypes.Delete, RequestActionTypes.REQUEST), remove);
  yield takeLatest(createActionType(LocalDocumentsActionTypes.Download, RequestActionTypes.REQUEST), download);
}
